// Global
import { useState } from 'react';

// Local
import { PrepMethod, RecipeResponse } from 'lib/services/models/content-response';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import styles from './RecipeInstructions.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { Field } from '@sitecore-content-sdk/nextjs';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import { RecipeDownloadTemplate } from '../../RecipeDownloadTemplate/RecipeDownloadTemplate';
import { RecipeBakeSection } from '../../RecipeBakeSection/RecipeBakeSection';

export interface RecipeInstructionsProps {
  recipe?: RecipeResponse;
}

export interface StepShot {
  Name: string;
  SortOrder: number;
  Url: string;
}

export interface StepShotProps {
  stepShots: StepShot[];
}

type AnnotatedStep = {
  direction: PrepMethod;
  isBake: boolean;
  stepNumber: number | null;
  descriptionSteps: string[];
};

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const hasHtmlTag = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const splitDescriptionIntoSteps = (description: string, isGMFS = false) => {
  const normalizedDescription = description.replace(/\r\n/g, '\n').replace(/<br\s*\/?>/gi, '\n');

  const expandedDescription = isGMFS
    ? normalizedDescription.replace(/<\/p>/gi, '</p>\n')
    : normalizedDescription;

  if (!expandedDescription.includes('\n')) {
    return [description];
  }

  const steps = expandedDescription
    .split(/\n+/)
    .map((step) => step.trim())
    .filter(Boolean);

  if (steps.length <= 1) {
    return [description];
  }

  return steps.map((step) => (hasHtmlTag(step) ? step : `<p>${escapeHtml(step)}</p>`));
};

export function RecipeInstructions(props: RecipeInstructionsProps) {
  const { recipe } = props;

  // Translations from dictionary
  const { getDictionaryValue } = useDictionary();
  const headingText = getDictionaryValue(
    'Components.Recipe.RecipeContentInstructions.Heading',
    'Instructions'
  );
  const hideImagesText = getDictionaryValue(
    'Components.Recipe.RecipeContentInstructions.HideImages',
    'Hide images'
  );
  const showImagesText = getDictionaryValue(
    'Components.Recipe.RecipeContentInstructions.ShowImages',
    'Show images'
  );

  // Site settings
  const { siteName } = useGlobalData();
  const siteSettings = useSiteSettings();
  const instructionType =
    (siteSettings?.recipeDetailSiteSettings?.fields?.instructionsView as Field<string>)?.value ||
    'List';

  // State for step shots
  const [showStepShots, setShowStepShots] = useState(true);
  const [toggleAnnouncement, setToggleAnnouncement] = useState('');
  const handleStepShotToggle = () => {
    const next = !showStepShots;
    setShowStepShots(next);
    const buttonLabel = getDictionaryValue(
      'Components.Recipe.RecipeContentInstructions.ButtonLabel',
      'button'
    );
    setToggleAnnouncement(`${next ? hideImagesText : showImagesText} ${buttonLabel}`);

    // Send GTM event
    const gtmEvent: GtmEvent = {
      event: showStepShots ? 'hide_step_shots' : 'show_step_shots',
    };

    pushToDataLayer({ ...gtmEvent } as DataLayerEvent);
  };

  // Variables
  const headingId = 'gmi-recipe-instructions-heading';
  const hasStepShots = recipe?.PrepMethods?.some((method) => method.StepShots?.length ?? 0 > 0);

  if (!recipe?.PrepMethods?.length) {
    return <></>;
  }

  function renderStepNumber(stepNumber: string) {
    const stepNumberText = getDictionaryValue(
      'Components.Recipe.RecipeContentInstructions.StepNumber',
      'Step {number}',
      [{ key: '{number}', value: stepNumber }]
    );
    return stepNumberText;
  }

  function getBestStepShotUrl(stepShots: StepShot[]) {
    const large = stepShots.find((stepShot) => stepShot.Name === 'StepShotLarge');
    if (large) return large.Url;

    const medium = stepShots.find((stepShot) => stepShot.Name === 'StepShotMedium');
    if (medium) return medium.Url;

    return stepShots.length > 0 ? stepShots[0].Url : null;
  }

  function StepShot({ stepShots }: StepShotProps) {
    const imgUrl = getBestStepShotUrl(stepShots);

    if (!imgUrl) {
      return <></>;
    }

    return (
      <EnhancedNextImage
        src={imgUrl}
        width="680" // I wish I had this from the data, but we are hardcoding it to 16:9 for now
        height="383"
        alt="" // No alt text available from the data
      />
    );
  }

  return (
    <div className={styles.recipeInstructions}>
      <RecipeDownloadTemplate recipe={recipe} />
      <div className={styles.recipeInstructionsHeader}>
        <h2 className={styles.recipeInstructionsHeading} id={headingId}>
          {headingText}
        </h2>

        {hasStepShots && instructionType === 'Card' && (
          <>
            <CtaButton
              className="printHide"
              ctaSize="small"
              ctaStyle="outline"
              ctaVariant="brand"
              text={showStepShots ? hideImagesText : showImagesText}
              onClick={handleStepShotToggle}
            />
            <span className={styles.srOnly} aria-live="polite" aria-atomic="true">
              {toggleAnnouncement}
            </span>
          </>
        )}
      </div>

      {(() => {
        // Pre-compute sequential step numbers, skipping 'Bake' entries
        let stepCount = 0;
        const annotatedSteps: AnnotatedStep[] = (recipe.PrepMethods ?? []).map((direction) => {
          const isBake = (direction.Header || '').trim().toLowerCase() === 'bake';
          if (isBake) {
            return { direction, isBake, stepNumber: null, descriptionSteps: [] };
          }

          stepCount++;

          const isGMFS = siteName === 'GMFS';
          const steps = splitDescriptionIntoSteps(direction.Description, isGMFS);

          return {
            direction,
            isBake,
            stepNumber: stepCount,
            descriptionSteps: steps,
          };
        });

        if (instructionType === 'Card') {
          return (
            <ol className={styles.recipeInstructionsSteps} aria-labelledby={headingId}>
              {annotatedSteps.map(({ direction, isBake, stepNumber, descriptionSteps }) =>
                isBake ? (
                  <li key={`bake-${direction.SortOrder}`}>
                    <RecipeBakeSection prepMethods={[direction]} />
                  </li>
                ) : (
                  <li key={direction.SortOrder}>
                    <ol start={stepNumber!} className={styles.recipeInstructionsCardList}>
                      <li id={`step${stepNumber}`}>
                        <div className={styles.recipeInstructionsCard}>
                          <div className={styles.recipeInstructionsCardText}>
                            <div className={styles.recipeInstructionsCardHeading}>
                              {direction.Header || renderStepNumber(String(stepNumber!))}
                            </div>
                            {descriptionSteps.length > 1 || siteName === 'GMFS' ? (
                              <ol className={styles.recipeInstructionsDescriptionList}>
                                {descriptionSteps.map((descriptionStep, index) => (
                                  <li key={index}>
                                    <EnhancedRichText
                                      value={descriptionStep}
                                      className={styles.recipeInstructionsCardDescription}
                                    />
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <EnhancedRichText
                                value={direction.Description}
                                className={styles.recipeInstructionsCardDescription}
                              />
                            )}
                          </div>
                          {direction.StepShots?.length && showStepShots && (
                            <div className={styles.recipeInstructionsCardImageContainer}>
                              <StepShot stepShots={direction.StepShots} />
                            </div>
                          )}
                        </div>
                      </li>
                    </ol>
                  </li>
                )
              )}
            </ol>
          );
        }

        return (
          <ol className={styles.recipeInstructionsSteps} aria-labelledby={headingId}>
            {annotatedSteps.map(({ direction, isBake, stepNumber, descriptionSteps }) =>
              isBake ? (
                <li key={`bake-${direction.SortOrder}`}>
                  <RecipeBakeSection prepMethods={[direction]} />
                </li>
              ) : (
                <li key={direction.SortOrder}>
                  <ol start={stepNumber!} className={styles.recipeInstructionsList}>
                    {descriptionSteps.length > 1 || siteName === 'GMFS' ? (
                      descriptionSteps.map((descriptionStep, index) => (
                        <li key={index} className={styles.recipeInstructionsListItem}>
                          <EnhancedRichText value={descriptionStep} />
                        </li>
                      ))
                    ) : (
                      <li id={`step${stepNumber}`} className={styles.recipeInstructionsListItem}>
                        <EnhancedRichText value={direction.Description} />
                      </li>
                    )}
                  </ol>
                </li>
              )
            )}
          </ol>
        );
      })()}
    </div>
  );
}
